import { useState } from 'react'
import { profileService } from '../../services/profileService'
import EducationForm from './EducationForm'
import type { Education, EducationInput } from '../../types'

interface EducationSectionProps {
  educations: Education[]
  onUpdate: (educations: Education[]) => void
}

export default function EducationSection({ educations, onUpdate }: EducationSectionProps) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const handleCreate = async (data: EducationInput) => {
    const newEdu = await profileService.createEducation(data)
    onUpdate([...educations, newEdu])
    setShowAddForm(false)
  }

  const handleUpdate = async (id: number, data: EducationInput) => {
    const updated = await profileService.updateEducation(id, data)
    onUpdate(educations.map((e) => (e.id === id ? updated : e)))
    setEditingId(null)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this education?')) return
    setDeletingId(id)
    try {
      await profileService.deleteEducation(id)
      onUpdate(educations.filter((e) => e.id !== id))
    } finally {
      setDeletingId(null)
    }
  }

  const formatYear = (dateStr: string | null) => {
    if (!dateStr) return ''
    return new Date(dateStr).getFullYear().toString()
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <svg
            className="w-5 h-5 text-emerald-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 14l9-5-9-5-9 5 9 5z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
            />
          </svg>
          Education
        </h2>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="mb-6">
          <EducationForm onSubmit={handleCreate} onCancel={() => setShowAddForm(false)} />
        </div>
      )}

      {educations.length === 0 && !showAddForm ? (
        <p className="text-gray-500 text-center py-8">No education added yet.</p>
      ) : (
        <div className="space-y-4">
          {educations.map((edu) =>
            editingId === edu.id ? (
              <EducationForm
                key={edu.id}
                education={edu}
                onSubmit={(data) => handleUpdate(edu.id, data)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <div
                key={edu.id}
                className="group p-4 border border-gray-100 rounded-xl hover:border-gray-200 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{edu.institution_name}</h3>
                    <p className="text-emerald-600">
                      {edu.degree}
                      {edu.field_of_study && ` in ${edu.field_of_study}`}
                    </p>
                    <div className="text-sm text-gray-500 mt-1">
                      {formatYear(edu.start_date)} - {formatYear(edu.end_date) || 'Present'}
                      {edu.gpa && <span className="ml-2">• GPA: {edu.gpa}</span>}
                    </div>
                    {edu.description && (
                      <p className="text-gray-600 mt-2 text-sm whitespace-pre-line">
                        {edu.description}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setEditingId(edu.id)}
                      className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(edu.id)}
                      disabled={deletingId === edu.id}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  )
}
